// review-candidate-session.ts
//
// P20, hướng A. The `review-local-v1` session reviews the CANDIDATE — the
// commit the mission is about to push — so it can only run once the ticket
// DAG is exhausted. That is a stage of REVIEWING, not a ticket.
//
// Why not a seeded bootstrap ticket (measured, not assumed): a ticket seeded
// at `autopilot init` can only depend on tickets that exist at init time, so
// `depends_on: ["T-0002"]` (plan) makes it ready IMMEDIATELY after planning —
// before a single ticket the plan produced has run. On a real e2e mission the
// report was promoted at SHA `1a8d9a18` while the release flow looked it up
// at `7cfac65a`. Making the plan worker declare the dependency instead would
// hand the ordering of its own review to the worker — the Bug K shape this
// project already paid for.
//
// So the ticket is built here, in memory, and dispatched through a projection
// containing only itself, at the point where `runUntilIdle` would otherwise
// return "done". It is never written to `.avatar/tickets/`: it is not part of
// the plan, `canAdvancePhase` must not wait on it, and a later run must not
// re-discover it as unfinished work.
import {
  CLEAN_REVIEW_CANDIDATE_TICKET_ID,
  REVIEW_CANDIDATE_TICKET_ID,
} from "./controller-reserved-ticket-ids.js";
import { type Ticket, TicketSchema } from "./schemas/ticket-schema.js";

// Ticket ids for the candidate review sessions — see controller-reserved-
// ticket-ids.ts for why the 9xxx range and why fixed ids. Re-exported here so
// existing call sites (supervisor-proposal-application.ts,
// clean-review-candidate-session.test.ts) keep importing from this module.
export {
  CLEAN_REVIEW_CANDIDATE_TICKET_ID,
  REVIEW_CANDIDATE_TICKET_ID,
} from "./controller-reserved-ticket-ids.js";

/**
 * The in-memory `review-local-v1` ticket. `allowed_paths` is empty on
 * purpose: the session's only write grant is its own attempt staging dir
 * (command-adapter-table.ts), and an empty scope means commit-owner stages
 * nothing — this attempt cannot author a commit and therefore cannot move the
 * candidate it was asked to review.
 */
export function buildReviewCandidateTicket(): Ticket {
  return TicketSchema.parse({
    id: REVIEW_CANDIDATE_TICKET_ID,
    title: "Review candidate (local)",
    kind: "review",
    revision: 1,
    executor: "claude",
    stage: "REVIEWING",
    slice_id: "release",
    depends_on: [],
    command_adapter: "review-local-v1",
    plan_path: ".avatar/PLAN.md",
    phase_path: ".avatar/plans/bootstrap.md",
    allowed_paths: [],
    verification_recipe: "review-candidate",
    required_capabilities: [],
    sources: [],
  });
}

/**
 * Terminal statuses for the review session — a session that reached one of
 * these is not dispatched again FOR THE SAME CANDIDATE. A `done` for an older
 * candidate is re-offered (code review P3 H1, supervisor-candidate-review-
 * settlement.ts); the predicates below are the status-only half of that check.
 *
 * `done` is the obvious case. `cancelled`/`superseded` mean a human or a
 * proposal took the session out of scope, and re-dispatching would override
 * that decision. Everything else (`todo` after a rejected attempt,
 * `in_progress` after a supervisor kill) SHOULD run again on the next `run` —
 * that is retry/orphan recovery, not a duplicate.
 */
const SETTLED_REVIEW_STATUSES = new Set(["done", "cancelled", "superseded"]);

/**
 * H1 (measured, not hypothetical): the dispatch site used to hard-code this
 * ticket's status as "todo" and guard re-dispatch with an in-memory flag. That
 * flag dies with the process, so a SECOND `avatar autopilot run` on an
 * already-finished mission dispatched the whole review session again —
 * measured on the release-flow e2e across two consecutive runs: 2
 * `attempt_started` events for T-9001. Each repeat burns a real Claude
 * session, and the promotion then overwrites the staged report. Same
 * non-idempotent-side-effect shape as Bug O (P18), which is why the check has
 * to read DURABLE state (MissionStore's ticket map, the same source
 * buildSchedulerProjection reads) rather than anything held in memory.
 */
export function reviewCandidateSessionAlreadyRan(
  missionTicketStatus: Record<string, string>,
): boolean {
  const status = missionTicketStatus[REVIEW_CANDIDATE_TICKET_ID];
  return status !== undefined && SETTLED_REVIEW_STATUSES.has(status);
}

/**
 * The in-memory `review-clean-v1` ticket — the second reviewer, dispatched
 * against the same candidate right after T-9001.
 *
 * `plan_path`/`phase_path` still carry the bootstrap values because
 * TicketSchema requires them (`.min(1)`), but they never reach the worker:
 * run-command-prompt-builder.ts strips both from this adapter's prompt AND
 * from the ticket JSON it embeds. That is the adapter's defining property —
 * see the CLEAN_REVIEW_ADAPTER_ID branch there.
 */
export function buildCleanReviewCandidateTicket(): Ticket {
  return TicketSchema.parse({
    id: CLEAN_REVIEW_CANDIDATE_TICKET_ID,
    title: "Review candidate (context sạch — reviewer thứ hai)",
    kind: "review",
    revision: 1,
    executor: "claude",
    stage: "REVIEWING",
    slice_id: "release",
    depends_on: [],
    command_adapter: "review-clean-v1",
    plan_path: ".avatar/PLAN.md",
    phase_path: ".avatar/plans/bootstrap.md",
    allowed_paths: [],
    verification_recipe: "review-candidate",
    required_capabilities: [],
    sources: [],
  });
}

/** Same durable-state guard as the first reviewer (H1), keyed on T-9002. */
export function cleanReviewCandidateSessionAlreadyRan(
  missionTicketStatus: Record<string, string>,
): boolean {
  const status = missionTicketStatus[CLEAN_REVIEW_CANDIDATE_TICKET_ID];
  return status !== undefined && SETTLED_REVIEW_STATUSES.has(status);
}
